import {
  Button,
  Checkbox,
  Chip,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger,
  Progress,
  Spinner,
  Switch,
  Tab,
  Tabs,
} from "@heroui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getCoreRowModel, useReactTable } from "@tanstack/react-table";
import {
  Columns3,
  Download,
  FilePlus2,
  FileSearch,
  FileText,
  MoreVertical,
  PackageCheck,
  PackageX,
  Plus,
  ReceiptText,
  RefreshCw,
  Trash2,
  Unlink,
  Upload,
} from "lucide-react";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import AuthorizedButton from "@/components/common/AuthorizedButton";
import ConfirmDialog from "@/components/common/ConfirmDialog";
import DetailField from "@/components/common/DetailField";
import EdgeScrollButton from "@/components/common/EdgeScrollButton";
import HintTooltip from "@/components/common/HintTooltip";
import PageHeader from "@/components/common/PageHeader";
import SingleFieldEditDialog, {
  type SingleFieldTarget,
} from "@/components/common/SingleFieldEditDialog";
import ColumnSettingsDialog from "@/components/table/ColumnSettingsDialog";
import DataTable from "@/components/table/DataTable";
import { listPartners } from "@/features/partners/api";
import { QUOTATION_STATUS_LABEL } from "@/features/quotations/api";
import { previewProjectSetProduct } from "@/features/setProduct/api";
import SetProductDialog from "@/features/setProduct/SetProductDialog";
import { listUsers, userLabel } from "@/features/users/api";
import { useHorizontalScroll } from "@/hooks/useHorizontalScroll";
import { useTableSettings } from "@/hooks/useTableSettings";
import { errorMessage } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { canWrite } from "@/lib/authorization";
import { showError, showSuccess } from "@/lib/toast";
import {
  createProjectItem,
  deleteProject,
  deleteProjectItem,
  deleteProjectItems,
  downloadProjectInvoice,
  downloadPurchaseOrder,
  getProject,
  listProjectItems,
  listPurchaseOrders,
  type ProjectInput,
  type ProjectItem,
  type ProjectItemInput,
  raiseQuotationFromProject,
  releaseQuotationOfProject,
  reorderProjectItems,
  selectQuotationForProject,
  setProjectSetProduct,
  shipProject,
  updateProject,
  updateProjectItem,
} from "./api";
import PurchaseOrderUploadDialog from "./PurchaseOrderUploadDialog";
import {
  buildProjectItemColumns,
  projectItemColumnIds,
  projectItemDefaultHiddenColumnIds,
  projectItemPinnedColumnIds,
} from "./itemColumns";
import ProjectDrawingsPanel from "./ProjectDrawingsPanel";
import { projectDrawingsQuery } from "./drawingsQuery";
import ItemQuotationLinkDialog from "./ItemQuotationLinkDialog";
import ProjectItemDialog from "./ProjectItemDialog";
import QuotationPickerDialog from "./QuotationPickerDialog";

/** 詳細画面のタブ。 */
type ProjectDetailTab = "info" | "items" | "drawings";

/** URL に載せられるタブかどうか（知らない値は既定のタブに倒す）。 */
function toTab(value: string | null): ProjectDetailTab {
  return value === "items" || value === "drawings" ? value : "info";
}

/**
 * ヘッダの 1 項目。表示値と、編集ダイアログの設定・保存内容をまとめて持つ。
 *
 * **編集できない項目もある**（起票元の見積など、別の画面へ移るだけのもの）。
 * その場合は `target` / `toInput` を持たず、`linkTo` を持つ。
 */
interface HeaderField {
  key: string;
  label: string;
  display: string | null;
  /** 値を押したときに移る先。入れると編集ではなくリンクになる。 */
  linkTo?: string;
  target?: Omit<SingleFieldTarget, "label">;
  toInput?: (value: string | null) => ProjectInput;
  /**
   * 1 項目編集のダイアログでは済まない項目の入口（セット品の切り替えなど）。
   * 入れると押したときにこれを呼ぶ。
   */
  action?: () => void;
  /** 値の代わりに置く部品（セット品のスイッチ）。一覧と操作を揃えるためのもの。 */
  control?: ReactNode;
}

/**
 * 案件詳細（案件管理画面）。
 *
 * **「案件情報」「明細」「図面」の 3 タブ**で切り替える（見積詳細と同じ作り）。
 * 以前はヘッダと明細を縦に積んでいたが、図面を足すと 1 画面に 3 つ入って
 * どれも中途半端な高さになる。タブなら開いているものに画面をすべて使える。
 *
 * 図面は受注後にも追図・差し替えが届くので、取り込みと突き合わせを独立した
 * タブにしてある（[[ProjectDrawingsPanel]]）。
 *
 * 扱うのは金額ではなく納期と出荷。
 */
export default function ProjectDetail() {
  const { projectId } = useParams();
  const id = Number(projectId);
  // 開いているタブは URL に持つ。リロードやブラウザの戻る/進むで元のタブに戻り、
  // 「この案件の図面を見て」と URL で渡せる（見積詳細と同じ方針）。
  const [searchParams, setSearchParams] = useSearchParams();
  const tab: ProjectDetailTab = toTab(searchParams.get("tab"));
  const setTab = (next: ProjectDetailTab) =>
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        // 既定のタブは URL に残さない（共有する URL を無駄に長くしない）。
        if (next === "info") params.delete("tab");
        else params.set("tab", next);
        return params;
      },
      { replace: true },
    );
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [uploadingPurchaseOrder, setUploadingPurchaseOrder] = useState(false);
  const [downloadingPo, setDownloadingPo] = useState(false);
  const [issuingInvoice, setIssuingInvoice] = useState(false);
  const [columnSettingsOpen, setColumnSettingsOpen] = useState(false);
  const { user } = useAuth();
  const editable = canWrite(user, "transactional");

  const {
    settings,
    setColumnVisibility,
    setColumnOrder,
    setColumnSizing,
    reset: resetColumns,
  } = useTableSettings(
    "project-items",
    projectItemColumnIds,
    projectItemPinnedColumnIds,
    projectItemDefaultHiddenColumnIds,
  );
  const { scrollRef, scrollBy, canScrollLeft, canScrollRight } = useHorizontalScroll();

  const [editingField, setEditingField] = useState<HeaderField | null>(null);
  /** セット品の切り替えの確認ダイアログ（切り替え先。null なら閉じている）。 */
  const [togglingSetProduct, setTogglingSetProduct] = useState<boolean | null>(null);
  const [addingItem, setAddingItem] = useState(false);
  const [deletingItem, setDeletingItem] = useState<ProjectItem | null>(null);
  /**
   * まとめて削除するために選んだ明細の id。
   *
   * 行そのものではなく id で持つ。明細は編集・並べ替えのたびに取り直すので、
   * 行を持つと選択が古い内容を指したままになる。
   */
  const [selectedItemIds, setSelectedItemIds] = useState<Set<number>>(new Set());
  const [bulkDeletingItems, setBulkDeletingItems] = useState(false);
  const [shipping, setShipping] = useState(false);
  const [deletingProject, setDeletingProject] = useState(false);
  /** 既にある見積を選ぶダイアログを開いているか（特急フロー）。 */
  const [pickingQuotation, setPickingQuotation] = useState(false);
  /** 見積明細との結び付けダイアログを開いている明細（null なら閉じている）。 */
  const [linkingQuotationItem, setLinkingQuotationItem] = useState<ProjectItem | null>(
    null,
  );

  const projectQuery = useQuery({
    queryKey: ["project", id],
    queryFn: () => getProject(id),
    enabled: Number.isFinite(id),
  });
  const itemsQuery = useQuery({
    queryKey: ["project-items", id],
    queryFn: () => listProjectItems(id),
    enabled: Number.isFinite(id),
  });
  // タブに件数を出すためだけに引く。図面タブ（[[ProjectDrawingsPanel]]）と
  // 同じキーなので、両方が表示されていても問い合わせは 1 回で済む。
  const drawingsQuery = useQuery({
    ...projectDrawingsQuery(id),
    enabled: Number.isFinite(id),
  });
  /**
   * 取り込んだ発注書。**読んでいる間だけ短い間隔で引き直す。**
   *
   * 読み取りは別プロセスのワーカーがやるので（[[purchase_orders]]）、起票した
   * 直後に明細が増える。図面タブのような SSE を足さずポーリングにしているのは、
   * 待つのが 1 案件につき数秒で、しかも**待ちが無くなれば止まる**ため
   * （`refetchInterval` が false を返す）。常時つなぎっぱなしの口を増やす
   * ほどの用途ではない。
   */
  const purchaseOrdersQuery = useQuery({
    queryKey: ["purchase-orders", id],
    queryFn: () => listPurchaseOrders(id),
    enabled: Number.isFinite(id),
    refetchInterval: (query) =>
      (query.state.data?.items ?? []).some((o) => o.status === "PENDING")
        ? 1500
        : false,
  });
  const partnersQuery = useQuery({
    queryKey: ["partners", "picker"],
    queryFn: () =>
      listPartners({ disabled: false, isClient: true, limit: 500, offset: 0 }),
  });
  // 直送先は用途が「直送先」の取引先だけ。得意先と同じマスタだが、候補に
  // 混ぜると送り先を取り違える。
  const shipTosQuery = useQuery({
    queryKey: ["partners", "shipTo", "picker"],
    queryFn: () =>
      listPartners({ disabled: false, isShipTo: true, limit: 500, offset: 0 }),
  });
  const usersQuery = useQuery({
    queryKey: ["users", "picker"],
    queryFn: () => listUsers(),
  });

  const project = projectQuery.data;
  const items = itemsQuery.data?.items ?? [];
  const purchaseOrders = purchaseOrdersQuery.data?.items ?? [];
  // ダウンロードは**最新の 1 通**を対象にする（取り込んだ順に並ぶ）。
  const latestPurchaseOrder = purchaseOrders[purchaseOrders.length - 1] ?? null;
  const readingCount = purchaseOrders.filter((o) => o.status === "PENDING").length;
  const readingPurchaseOrder = readingCount > 0;

  // **読み終わった瞬間に一覧を取り直す。** ワーカーが明細を作り、案件の空いて
  // いる欄（発注書番号・受注日・納期）も埋めるので、どちらも古くなっている。
  const doneCount = purchaseOrders.filter((o) => o.status !== "PENDING").length;
  useEffect(() => {
    if (doneCount === 0) return;
    void queryClient.invalidateQueries({ queryKey: ["project-items", id] });
    void queryClient.invalidateQueries({ queryKey: ["project", id] });
  }, [doneCount, id, queryClient]);

  /** 最新の発注書 PDF を保存する。 */
  const downloadPo = async () => {
    if (!latestPurchaseOrder) return;
    setDownloadingPo(true);
    try {
      await downloadPurchaseOrder(id, latestPurchaseOrder.purchase_order_id);
    } catch (e) {
      showError(errorMessage(e, "発注書をダウンロードできませんでした。"));
    } finally {
      setDownloadingPo(false);
    }
  };

  /**
   * 請求書 PDF を保存する。
   *
   * 出せない案件（得意先・明細・単価が欠けている）は API が 400 で止めるので、
   * その理由をそのまま出す。金額の空いた請求書を送ると過少請求になり、
   * 気づくのが入金の突合まで遅れるため（[[invoice_service]]）。
   */
  const issueInvoice = async () => {
    setIssuingInvoice(true);
    try {
      await downloadProjectInvoice(id);
    } catch (e) {
      showError(errorMessage(e, "請求書を発行できませんでした。"));
    } finally {
      setIssuingInvoice(false);
    }
  };

  const invalidate = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["project", id] }),
      queryClient.invalidateQueries({ queryKey: ["project-items", id] }),
      queryClient.invalidateQueries({ queryKey: ["projects"] }),
    ]);
  };

  /**
   * 案件の明細をコピーして見積を起こす（特急フロー）。
   *
   * 起こしたらそのまま見積の画面へ移る。次にやることは金額を入れることで、
   * 案件の画面に留まってもできることが無い。
   */
  const raiseQuotation = useMutation({
    mutationFn: () => raiseQuotationFromProject(id),
    onSuccess: async (quotation) => {
      await invalidate();
      showSuccess(`見積 ${quotation.quotation_number} を起こしました。`);
      navigate(`/quotations/${quotation.quotation_id}`);
    },
    onError: (e: Error) => showError(e.message),
  });

  const selectQuotation = useMutation({
    mutationFn: (quotationId: number) => selectQuotationForProject(id, quotationId),
    onSuccess: async (quotation) => {
      await invalidate();
      setPickingQuotation(false);
      showSuccess(`見積 ${quotation.quotation_number} を結び付けました。`);
    },
    onError: (e: Error) => showError(e.message),
  });

  const releaseQuotation = useMutation({
    mutationFn: () => releaseQuotationOfProject(id),
    onSuccess: async (quotation) => {
      await invalidate();
      showSuccess(`見積 ${quotation.quotation_number} との結び付けを外しました。`);
    },
    onError: (e: Error) => showError(e.message),
  });

  const headerFields = useMemo<HeaderField[]>(() => {
    if (!project) return [];
    const partners = partnersQuery.data?.items ?? [];
    const shipTos = shipTosQuery.data?.items ?? [];
    const users = usersQuery.data?.items ?? [];

    const date = (
      key: string,
      label: string,
      value: string | null,
      field: keyof ProjectInput,
    ): HeaderField => ({
      key,
      label,
      display: value,
      target: { value, kind: "date" },
      toInput: (v) => ({ [field]: v }) as ProjectInput,
    });

    return [
      {
        key: "project_title",
        label: "案件名",
        display: project.project_title,
        target: { value: project.project_title },
        toInput: (v) => ({ project_title: v }),
      },
      {
        key: "client",
        label: "得意先",
        display: project.client_name,
        target: {
          value: project.client_id != null ? String(project.client_id) : null,
          kind: "select",
          options: partners.map((p) => ({
            key: String(p.partner_id),
            label: `${p.partner_code} ${p.partner_name}`,
          })),
        },
        toInput: (v) => ({ client_id: v == null ? null : Number(v) }),
      },
      {
        // 得意先とは別の場所へ納める案件。空欄なら直送しない。
        key: "ship_to",
        label: "直送先",
        display: project.ship_to_name,
        target: {
          value: project.ship_to_id != null ? String(project.ship_to_id) : null,
          kind: "select",
          description:
            "得意先とは別の場所へ納めるときに選びます。選択肢は取引先マスタで用途に「直送先」が入っているものです。空欄なら直送しません。",
          options: shipTos.map((p) => ({
            key: String(p.partner_id),
            label: `${p.partner_code} ${p.partner_name}`,
          })),
        },
        toInput: (v) => ({ ship_to_id: v == null ? null : Number(v) }),
      },
      {
        key: "client_po_number",
        label: "客先注文番号",
        display: project.client_po_number,
        target: { value: project.client_po_number },
        toInput: (v) => ({ client_po_number: v }),
      },
      {
        // セット品の切り替えは**明細の形も変える**（畳む ⇔ 客先購番ごとに分ける）
        // ので、押すと確認ダイアログが開く。見た目と操作は一覧の列と揃えて
        // スイッチにする。
        key: "is_set_product",
        label: "セット品",
        display: project.is_set_product ? "セット品" : null,
        control: (
          <Switch
            size="sm"
            isSelected={project.is_set_product}
            isDisabled={!editable}
            onValueChange={() => setTogglingSetProduct(!project.is_set_product)}
            aria-label="セット品"
            classNames={{ base: "m-0 p-0", label: "hidden" }}
          />
        ),
      },
      {
        key: "set_count",
        label: "セット数",
        display: project.set_count == null ? null : `${project.set_count} セット`,
        // 客先購番が付いている案件のセット数は**購番の本数から決まる導出値**なので
        // 直せない（API も 409 で断る）。紙の無い案件だけ手で入れられる。
        target:
          project.client_order_numbers.length > 0
            ? undefined
            : {
                value: project.set_count == null ? null : String(project.set_count),
                kind: "number",
                description:
                  "何セット作るか。入れるとセット品になります。明細の数量は「1 セットあたり × セット数」の合算です。",
              },
        toInput: (v) => ({ set_count: v == null || v === "" ? null : Number(v) }),
      },
      {
        // 畳む前の購番（[[client_order_lines]]）。セット品は客先がセット 1 本ごとに
        // 購番を切るので複数本並ぶ。**直せない**（紙の行そのものの記録で、
        // 取り込みで決まる値）。客先からの問い合わせはこの番号で来る。
        key: "client_order_numbers",
        label: "客先購番",
        display:
          project.client_order_numbers.length === 0
            ? null
            : project.client_order_numbers.join(" / "),
      },
      {
        // 起票元の見積。**いくらで受けたのか**を辿る入口で、見積の側からは工番が
        // 見えるのに案件の側から見積が見えないと、一覧を検索し直すことになる。
        // 紐付けは明細が持つ（手で作った案件には起票元が無いので「—」になる）。
        //
        // 特急フローでは**確定する前の見積**（作りかけ）もここに出す。出さないと、
        // 起こした見積へ戻る道が無く、一覧から探し直すことになる。確定したものと
        // 見分けが付くよう、確定前は状態を添える（[[urgent-project-flow]]）。
        key: "source_quotation",
        label: "見積",
        display:
          project.source_quotation_number ??
          (project.raised_quotation_number == null
            ? null
            : `${project.raised_quotation_number}（${
                project.raised_quotation_status == null
                  ? ""
                  : QUOTATION_STATUS_LABEL[project.raised_quotation_status]
              }）`),
        linkTo:
          project.source_quotation_id != null
            ? `/quotations/${project.source_quotation_id}`
            : project.raised_quotation_id != null
              ? `/quotations/${project.raised_quotation_id}`
              : undefined,
      },
      {
        key: "sales_rep",
        label: "営業担当",
        display: users.find((u) => u.user_id === project.sales_rep_id)?.name ?? null,
        target: {
          value: project.sales_rep_id,
          kind: "select",
          options: users.map((u) => ({ key: u.user_id, label: userLabel(u) })),
        },
        toInput: (v) => ({ sales_rep_id: v }),
      },
      date("order_date", "受注日", project.order_date, "order_date"),
      date("deadline", "納期", project.deadline, "deadline"),
      date(
        "shipping_planned_date",
        "出荷予定日",
        project.shipping_planned_date,
        "shipping_planned_date",
      ),
      {
        key: "note",
        label: "備考",
        display: project.note,
        target: { value: project.note, kind: "textarea" },
        toInput: (v) => ({ note: v }),
      },
    ];
    // editable はセット品のスイッチを押せるかどうかに効く（権限が変われば
    // 項目も組み直す）。
  }, [project, partnersQuery.data, shipTosQuery.data, usersQuery.data, editable]);

  const saveHeaderField = async (value: string | null): Promise<string | null> => {
    if (!editingField?.toInput) return null;
    try {
      await updateProject(id, editingField.toInput(value));
      await invalidate();
      return null;
    } catch (e) {
      return errorMessage(e, "保存に失敗しました。");
    }
  };

  const saveItemField = async (
    item: ProjectItem,
    input: ProjectItemInput,
  ): Promise<string | null> => {
    try {
      await updateProjectItem(id, item.project_item_id, input);
      await invalidate();
      return null;
    } catch (e) {
      return errorMessage(e, "保存に失敗しました。");
    }
  };

  const submitItem = async (input: ProjectItemInput): Promise<string | null> => {
    try {
      await createProjectItem(id, input);
      await invalidate();
      return null;
    } catch (e) {
      return errorMessage(e, "保存に失敗しました。");
    }
  };

  const reorder = useMutation({
    mutationFn: (ids: number[]) => reorderProjectItems(id, ids),
    onSuccess: () => void invalidate(),
    onError: (e: Error) => showError(e.message),
  });

  /** 掴んだ行を、落とした行の位置へ移す（先頭列のハンドルでのドラッグ）。 */
  const moveTo = (draggedId: number, targetId: number) => {
    const ids = items.map((i) => i.project_item_id);
    const from = ids.indexOf(draggedId);
    const to = ids.indexOf(targetId);
    if (from < 0 || to < 0 || from === to) return;
    const [moved] = ids.splice(from, 1);
    ids.splice(to, 0, moved);
    reorder.mutate(ids);
  };

  /** 1 行ぶん隣と入れ替える（ハンドルでの ↑↓ キー操作）。 */
  const moveBy = (item: ProjectItem, direction: -1 | 1) => {
    const ids = items.map((i) => i.project_item_id);
    const from = ids.indexOf(item.project_item_id);
    const to = from + direction;
    if (from < 0 || to < 0 || to >= ids.length) return;
    [ids[from], ids[to]] = [ids[to], ids[from]];
    reorder.mutate(ids);
  };

  const removeItem = useMutation({
    mutationFn: (item: ProjectItem) => deleteProjectItem(id, item.project_item_id),
    onSuccess: (_, item) => {
      showSuccess(`${item.sequence} 行目を削除しました。`);
      void invalidate();
    },
    onError: (e: Error) => showError(e.message),
    onSettled: () => setDeletingItem(null),
  });

  /**
   * 選ばれている明細。**毎回 items から引き直す**ので、消えた行の id が選択に
   * 残っていても無視される。
   */
  const selectedItems = items.filter((i) => selectedItemIds.has(i.project_item_id));
  const allItemsSelected = items.length > 0 && selectedItems.length === items.length;

  const toggleSelectItem = (item: ProjectItem) =>
    setSelectedItemIds((prev) => {
      const next = new Set(prev);
      if (next.has(item.project_item_id)) next.delete(item.project_item_id);
      else next.add(item.project_item_id);
      return next;
    });

  const toggleSelectAllItems = () =>
    setSelectedItemIds(
      allItemsSelected ? new Set() : new Set(items.map((i) => i.project_item_id)),
    );

  const removeItems = useMutation({
    mutationFn: (ids: number[]) => deleteProjectItems(id, ids),
    onSuccess: async (_data, ids) => {
      await invalidate();
      setSelectedItemIds(new Set());
      showSuccess(`${ids.length} 行を削除しました。`);
    },
    onError: (e: Error) => showError(e.message),
    onSettled: () => setBulkDeletingItems(false),
  });

  const ship = useMutation({
    mutationFn: (shipped: boolean) => shipProject(id, shipped),
    onSuccess: (updated) => {
      showSuccess(updated.shipped_at ? "出荷済みにしました。" : "未出荷に戻しました。");
      void invalidate();
    },
    onError: (e: Error) => showError(e.message),
    onSettled: () => setShipping(false),
  });

  const removeProject = useMutation({
    mutationFn: () => deleteProject(id),
    onSuccess: () => {
      showSuccess(`案件 ${project?.project_number} を削除しました。`);
      void queryClient.invalidateQueries({ queryKey: ["projects"] });
      navigate("/projects");
    },
    onError: (e: Error) => showError(e.message),
    onSettled: () => setDeletingProject(false),
  });

  const columns = useMemo(
    () =>
      buildProjectItemColumns({
        canWrite: editable,
        itemCount: items.length,
        selectedIds: selectedItemIds,
        onToggleSelect: toggleSelectItem,
        onSaveField: saveItemField,
        onOpenQuotationLink: setLinkingQuotationItem,
        onMove: moveBy,
        onDelete: setDeletingItem,
      }),
    // saveItemField / move は毎回作り直されるが、中身は id と items に依存する。
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editable, items.length, selectedItemIds],
  );

  const table = useReactTable<ProjectItem>({
    data: items,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => String(row.project_item_id),
    columnResizeMode: "onChange",
    // 並び順は案件の行番号そのもの。列ヘッダでの並び替えは持たない。
    enableSorting: false,
    state: {
      columnVisibility: settings.columnVisibility,
      columnOrder: settings.columnOrder,
      columnSizing: settings.columnSizing,
    },
    onColumnVisibilityChange: setColumnVisibility,
    onColumnOrderChange: setColumnOrder,
    onColumnSizingChange: setColumnSizing,
    defaultColumn: { minSize: 60, maxSize: 600 },
  });

  if (projectQuery.isPending) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner label="読み込み中..." />
      </div>
    );
  }

  if (projectQuery.isError || !project) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4">
        <p className="text-default-600">
          案件を取得できませんでした。
          <br />
          {(projectQuery.error as Error | null)?.message}
        </p>
        <Button color="primary" onPress={() => navigate("/projects")}>
          案件一覧へ戻る
        </Button>
      </div>
    );
  }

  const shipped = project.shipped_at != null;
  const unlinked = items.filter((i) => i.quotation_item_id == null).length;
  const drawingCount = drawingsQuery.data?.items.length ?? 0;
  /**
   * 見積を設定できるか。**明細と図面の両方が入ってから**。
   *
   * 見積は「何を」「どの図面で」作るかが決まってから起こすもの。空の案件から
   * 起こすと中身の無い見積だけが残り、図面が無ければ金額を入れる画面で図面を
   * 見られない（[[urgent-project-flow]]）。
   */
  const quotationReady = items.length > 0 && drawingCount > 0;
  const unmatchedDrawings = drawingsQuery.data?.unmatched_count ?? 0;

  return (
    <div className="relative h-full">
      <div className="flex h-full flex-col px-6 pt-3 pb-3">
        <section className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl bg-white shadow-md">
          <PageHeader
            title={project.project_number}
            parents={[{ label: "案件管理", to: "/projects" }]}
            actions={
              <>
                <Chip size="sm" variant="flat" color={shipped ? "success" : "default"}>
                  {shipped ? `出荷済 ${project.shipped_at?.slice(0, 10)}` : "未出荷"}
                </Chip>
                {/* 特急フローの入口。**見積がまだ 1 つも無い案件にだけ出す**
                    （[[urgent-project-flow]]）。受注で起票された案件には既に
                    見積があるので出てこない。

                    起こすのと選ぶのは「この案件の見積を決める」という 1 つの仕事
                    なので、ボタンは 1 つにしてメニューで出し分ける。横に 2 つ
                    並べると、どちらを押すのか毎回読むことになる。 */}
                {project.source_quotation_id == null &&
                  project.raised_quotation_id == null && (
                    <HintTooltip
                      content={
                        quotationReady
                          ? "この案件の見積を起こすか、既にある見積を選びます。"
                          : "明細と図面を入れてから設定できます。"
                      }
                    >
                      <span className="inline-flex">
                        <Dropdown isDisabled={!quotationReady}>
                          <DropdownTrigger>
                            <AuthorizedButton
                              scope="transactional"
                              radius="full"
                              variant="bordered"
                              data-testid="quotation-setup"
                              startContent={<FileText size={18} />}
                              // **明細と図面が入るまで押させない**。見積は
                              // 「何を」「どの図面で」作るかが決まってから起こす
                              // もので、空の案件から起こすと中身の無い見積が
                              // 残る（[[urgent-project-flow]]）。
                              isDisabled={!quotationReady}
                              isLoading={raiseQuotation.isPending}
                            >
                              見積設定
                            </AuthorizedButton>
                          </DropdownTrigger>
                          <DropdownMenu
                            aria-label="見積設定"
                            onAction={(key) => {
                              if (key === "raise") raiseQuotation.mutate();
                              if (key === "pick") setPickingQuotation(true);
                            }}
                          >
                            <DropdownItem
                              key="raise"
                              data-testid="raise-quotation"
                              startContent={<FilePlus2 size={18} />}
                              description="明細をコピーして新しい見積を作ります"
                            >
                              見積を起こす
                            </DropdownItem>
                            <DropdownItem
                              key="pick"
                              data-testid="pick-quotation"
                              startContent={<FileSearch size={18} />}
                              description="客先から届いている見積を選びます"
                            >
                              既にある見積を選ぶ
                            </DropdownItem>
                          </DropdownMenu>
                        </Dropdown>
                      </span>
                    </HintTooltip>
                  )}
                <AuthorizedButton
                  scope="transactional"
                  radius="full"
                  color={shipped ? "default" : "primary"}
                  variant={shipped ? "bordered" : "solid"}
                  startContent={
                    shipped ? <PackageX size={18} /> : <PackageCheck size={18} />
                  }
                  onPress={() => setShipping(true)}
                >
                  {shipped ? "出荷を取り消す" : "出荷する"}
                </AuthorizedButton>
                <Dropdown>
                  <DropdownTrigger>
                    <Button isIconOnly variant="light" radius="full" aria-label="操作">
                      <MoreVertical size={20} className="text-brand-500" />
                    </Button>
                  </DropdownTrigger>
                  <DropdownMenu
                    aria-label="操作"
                    onAction={(key) => {
                      if (key === "reload") void invalidate();
                      if (key === "invoice") void issueInvoice();
                      if (key === "release-quotation") releaseQuotation.mutate();
                      if (key === "delete") setDeletingProject(true);
                    }}
                    disabledKeys={[
                      ...(editable ? [] : ["delete", "release-quotation"]),
                      // 確定前の見積にだけ効く。受注のあとは受注の取り消し。
                      ...(project.raised_quotation_id != null &&
                      project.source_quotation_id == null
                        ? []
                        : ["release-quotation"]),
                    ]}
                  >
                    <DropdownItem key="reload" startContent={<RefreshCw size={18} />}>
                      再読み込み
                    </DropdownItem>
                    {/* 請求書はこの案件 1 件ぶんを 1 通で出す。得意先・明細・
                        単価が欠けている案件は API が止めて理由を返す。 */}
                    <DropdownItem
                      key="invoice"
                      data-testid="issue-invoice"
                      startContent={<ReceiptText size={18} />}
                      description="この案件の明細から PDF を作ります"
                    >
                      {issuingInvoice ? "請求書を発行中…" : "請求書発行"}
                    </DropdownItem>
                    <DropdownItem
                      key="release-quotation"
                      startContent={<Unlink size={18} />}
                      description="見積そのものは消しません"
                    >
                      見積との結び付けを外す
                    </DropdownItem>
                    <DropdownItem
                      key="delete"
                      className="text-danger"
                      color="danger"
                      startContent={<Trash2 size={18} />}
                    >
                      この案件を削除
                    </DropdownItem>
                  </DropdownMenu>
                </Dropdown>
              </>
            }
          />
          {/* 見積詳細と枠が同じで見分けが付かないので、タブ行を塗る。一覧では
              ヘッダ行で同じことをしている（[[ProjectList]]）。淡い地なので
              タブの文字も下線もそのまま使える（濃く塗って白へ反転する案は
              差し戻された。[[match-flutter-ui-of-ngtg-msc]]）。 */}
          <div className="border-b border-brand-300 bg-brand-100 px-6">
            <Tabs
              aria-label="案件の表示切り替え"
              selectedKey={tab}
              onSelectionChange={(key) => setTab(key as ProjectDetailTab)}
              variant="underlined"
              color="primary"
              classNames={{ tabList: "gap-6 p-0", tab: "h-11 px-0" }}
            >
              <Tab key="info" title="案件情報" />
              <Tab
                key="items"
                title={
                  <span className="flex items-center gap-1.5">
                    明細
                    <span className="rounded-full bg-white px-2 text-tiny text-default-600">
                      {items.length}
                    </span>
                    {/* 見積と結び付いていない行は「いくらで受けたのか」を辿れない。
                        件数とは別に目立たせる（0 件のときは出さない）。 */}
                    {unlinked > 0 && (
                      <span className="rounded-full bg-warning-100 px-2 text-tiny font-bold text-warning-700">
                        見積未紐付 {unlinked}
                      </span>
                    )}
                  </span>
                }
              />
              <Tab
                key="drawings"
                title={
                  <span className="flex items-center gap-1.5">
                    図面
                    <span className="rounded-full bg-white px-2 text-tiny text-default-600">
                      {drawingCount}
                    </span>
                    {/* 紐付無しはこのタブでやる仕事そのものなので、件数とは別に
                        目立たせる。0 件のときは出さない。 */}
                    {unmatchedDrawings > 0 && (
                      <span className="rounded-full bg-warning-100 px-2 text-tiny font-bold text-warning-700">
                        紐付無し {unmatchedDrawings}
                      </span>
                    )}
                  </span>
                }
              />
            </Tabs>
          </div>

          {/* 読み取っている間はタブの直下にバーを出す。**終わるまで何も起きない
              ように見える**のを防ぐためのもの（明細は読み終えてから増える）。
              進み具合は分からないので不定（isIndeterminate）にしてある。
              ワーカーが 1 通ずつ片付けるので、複数あっても 1 本だけ出す。 */}
          {readingPurchaseOrder && (
            <div className="px-6 pt-2">
              <Progress
                size="sm"
                color="primary"
                isIndeterminate
                aria-label="発注書の読み取り"
                label={
                  readingCount > 1
                    ? `発注書を読み取っています…（残り ${readingCount} 通）`
                    : "発注書を読み取っています…"
                }
              />
            </div>
          )}

          {/* 読み取りの結果。**読めなかったことに気づけるようにするためのもの。**
              検算（数量 x 単価 と金額・小計）が合わない紙は明細を作らないので、
              ここに出さないと「上げたのに何も起きない」になる。
              読み取り中はバーが出ているので、ここには出さない。 */}
          {purchaseOrders
            .filter((order) => order.status !== "READY" && order.status !== "PENDING")
            .map((order) => (
              <div
                key={order.purchase_order_id}
                className="mx-6 mt-2 rounded-md border border-warning-200 bg-warning-50 px-3 py-2 text-sm text-warning-700"
              >
                <span className="font-bold">{order.original_filename ?? "発注書"}</span>
                {order.status === "NEEDS_REVIEW" &&
                  `：${order.error ?? "内容をご確認ください。"}明細は作っていません。`}
                {order.status === "FAILED" &&
                  `：${order.error ?? "読み取れませんでした。"}`}
              </div>
            ))}

          {tab === "info" ? (
            /* 案件情報は畳まずに全項目を出す。 */
            <div className="min-h-0 flex-1 overflow-auto px-6 py-2">
              <div className="grid grid-cols-1 gap-x-8 lg:grid-cols-2">
                {headerFields.map((field) => (
                  <DetailField
                    key={field.key}
                    label={field.label}
                    value={field.display}
                    canWrite={
                      editable && (field.target != null || field.action != null)
                    }
                    linkTo={field.linkTo}
                    control={field.control}
                    onEdit={() =>
                      field.action ? field.action() : setEditingField(field)
                    }
                  />
                ))}
              </div>
            </div>
          ) : tab === "drawings" ? (
            <ProjectDrawingsPanel projectId={id} canWrite={editable} items={items} />
          ) : (
            <>
              <div className="flex min-h-9 flex-wrap items-center gap-x-2.5 gap-y-2 px-6 py-3">
                <span className="text-sm text-default-500">
                  {items.length} 行{/* 選んだ件数は、削除が何行に効くかを示す。 */}
                  {selectedItems.length > 0 && (
                    <span className="ml-2 font-bold text-brand-600">
                      {selectedItems.length} 行を選択中
                    </span>
                  )}
                </span>
                {unlinked > 0 && (
                  <Chip size="sm" variant="flat" color="warning">
                    見積未紐付 {unlinked} 行
                  </Chip>
                )}
                {/* 全行の選択はここで 1 回押せば済む（表のヘッダは並び替えの
                    ボタンで埋まっており、中にチェックボックスを置けない）。 */}
                {editable && items.length > 0 && (
                  <Checkbox
                    size="sm"
                    isSelected={allItemsSelected}
                    isIndeterminate={selectedItems.length > 0 && !allItemsSelected}
                    onValueChange={toggleSelectAllItems}
                    data-testid="item-select-all"
                  >
                    <span className="text-sm">すべて選択</span>
                  </Checkbox>
                )}
                <div className="ml-auto flex flex-wrap items-center gap-2">
                  {/* **削除は選んでから**。常に出しておくと、押し間違えたときに
                      何行消えるのかが画面から読めない。 */}
                  {selectedItems.length > 0 && (
                    <AuthorizedButton
                      scope="transactional"
                      radius="full"
                      variant="light"
                      color="danger"
                      data-testid="item-bulk-delete"
                      startContent={<Trash2 size={18} />}
                      isLoading={removeItems.isPending}
                      onPress={() => setBulkDeletingItems(true)}
                    >
                      選んだ {selectedItems.length} 行を削除
                    </AuthorizedButton>
                  )}
                  <AuthorizedButton
                    scope="transactional"
                    radius="full"
                    color="primary"
                    data-testid="item-create"
                    startContent={<Plus size={18} />}
                    onPress={() => setAddingItem(true)}
                  >
                    明細を追加
                  </AuthorizedButton>
                  {/* 発注書はここから上げ直せる（客先が内容を変えて再発行する）。
                      いまある明細に無い行だけが足される。 */}
                  <AuthorizedButton
                    scope="transactional"
                    radius="full"
                    variant="bordered"
                    data-testid="purchase-order-upload"
                    startContent={<Upload size={18} />}
                    onPress={() => setUploadingPurchaseOrder(true)}
                  >
                    発注書をアップロード
                  </AuthorizedButton>
                  {/* 毎回は使わない操作はケバブに畳む（ツールバーに常時出すのは
                      「明細を追加」と「発注書をアップロード」の 2 つだけ）。 */}
                  <Dropdown>
                    <DropdownTrigger>
                      <Button
                        isIconOnly
                        variant="light"
                        radius="full"
                        aria-label="表示設定"
                        data-testid="items-menu"
                      >
                        <MoreVertical size={20} className="text-brand-500" />
                      </Button>
                    </DropdownTrigger>
                    <DropdownMenu
                      aria-label="表示設定"
                      disabledKeys={latestPurchaseOrder == null ? ["download_po"] : []}
                      onAction={(key) => {
                        if (key === "download_po") void downloadPo();
                        if (key === "columns") setColumnSettingsOpen(true);
                        if (key === "reset_columns") resetColumns();
                      }}
                    >
                      <DropdownItem
                        key="download_po"
                        startContent={<Download size={18} />}
                        // 1 通も無ければ選べない（選べて何も起きないのを避ける）。
                        description={
                          latestPurchaseOrder == null
                            ? "取り込んだ発注書がありません"
                            : undefined
                        }
                      >
                        {downloadingPo
                          ? "発注書をダウンロード中…"
                          : "発注書をダウンロード"}
                      </DropdownItem>
                      <DropdownItem key="columns" startContent={<Columns3 size={18} />}>
                        表示列の設定
                      </DropdownItem>
                      <DropdownItem
                        key="reset_columns"
                        startContent={<Columns3 size={18} />}
                      >
                        表示列リセット
                      </DropdownItem>
                    </DropdownMenu>
                  </Dropdown>
                </div>
              </div>

              <div className="min-h-0 flex-1">
                {itemsQuery.isError ? (
                  <div className="flex h-full flex-col items-center justify-center gap-4">
                    <p className="text-default-600">
                      明細を取得できませんでした。
                      <br />
                      {(itemsQuery.error as Error).message}
                    </p>
                    <Button color="primary" onPress={() => void itemsQuery.refetch()}>
                      再読み込み
                    </Button>
                  </div>
                ) : itemsQuery.isPending ? (
                  <div className="flex h-full items-center justify-center">
                    <Spinner label="読み込み中..." />
                  </div>
                ) : (
                  <DataTable
                    table={table}
                    isLoading={itemsQuery.isFetching}
                    emptyMessage="明細がありません。「明細を追加」から図面を登録してください。"
                    scrollRef={scrollRef}
                    onReorderRow={(draggedId, targetId) =>
                      moveTo(Number(draggedId), Number(targetId))
                    }
                  />
                )}
              </div>
            </>
          )}
        </section>
      </div>

      {/* 横スクロールは明細の表にだけ要る。 */}
      {tab === "items" && (
        <>
          <EdgeScrollButton
            side="left"
            isDisabled={!canScrollLeft}
            onPress={() => scrollBy(-1)}
          />
          <EdgeScrollButton
            side="right"
            isDisabled={!canScrollRight}
            onPress={() => scrollBy(1)}
          />
        </>
      )}

      {togglingSetProduct != null && project != null && (
        <SetProductDialog
          owner="project"
          number={project.project_number}
          to={togglingSetProduct}
          previewKey={["project-set-product-preview", id, togglingSetProduct]}
          fetchPreview={() => previewProjectSetProduct(id, togglingSetProduct)}
          onClose={() => setTogglingSetProduct(null)}
          onConfirm={async () => {
            await setProjectSetProduct(id, togglingSetProduct);
            // 明細の行数が変わるので、ヘッダと明細の両方を取り直す。
            await queryClient.invalidateQueries({ queryKey: ["project", id] });
            await queryClient.invalidateQueries({ queryKey: ["project-items", id] });
            await queryClient.invalidateQueries({ queryKey: ["projects"] });
            setTogglingSetProduct(null);
            showSuccess(
              togglingSetProduct ? "セット品にしました。" : "セット品を解除しました。",
            );
          }}
        />
      )}

      <SingleFieldEditDialog
        target={
          editingField?.target
            ? { label: editingField.label, ...editingField.target }
            : null
        }
        onClose={() => setEditingField(null)}
        onSave={saveHeaderField}
      />

      {/* 突き合わせで決まらなかった行を 1 行ずつ結ぶ（[[urgent-project-flow]]）。 */}
      <ItemQuotationLinkDialog
        projectId={id}
        item={linkingQuotationItem}
        canWrite={editable}
        onClose={() => setLinkingQuotationItem(null)}
      />

      {/* 客先から後で届いた見積をこの案件のものにする（特急フロー）。 */}
      <QuotationPickerDialog
        isOpen={pickingQuotation}
        clientId={project.client_id}
        isBusy={selectQuotation.isPending}
        onClose={() => setPickingQuotation(false)}
        onSelect={(quotation) => selectQuotation.mutate(quotation.quotation_id)}
      />

      <ProjectItemDialog
        isOpen={addingItem}
        defaultDeadline={project.deadline}
        defaultShippingPlannedDate={project.shipping_planned_date}
        onClose={() => setAddingItem(false)}
        onSubmit={submitItem}
      />

      <ConfirmDialog
        isOpen={bulkDeletingItems}
        title="選んだ明細の削除"
        message={`選んだ ${selectedItems.length} 行を削除します。\nこの操作は取り消せません。よろしいですか？\n\n行番号は詰めません。`}
        confirmLabel="削除"
        isDanger
        isBusy={removeItems.isPending}
        onClose={() => setBulkDeletingItems(false)}
        onConfirm={() =>
          removeItems.mutate(selectedItems.map((i) => i.project_item_id))
        }
      />

      <ConfirmDialog
        isOpen={shipping}
        title={shipped ? "出荷の取り消し" : "出荷の記録"}
        message={
          shipped
            ? `${project.project_number} を未出荷に戻します。記録間違いを直すための操作です。`
            : `${project.project_number} を出荷済みにします。\n出荷日時は現在時刻になります。\n\n一部だけ先に出荷する（分納）ことは、現在の仕組みでは記録できません。`
        }
        confirmLabel={shipped ? "取り消す" : "出荷する"}
        isBusy={ship.isPending}
        onClose={() => setShipping(false)}
        onConfirm={() => ship.mutate(!shipped)}
      />

      <ConfirmDialog
        isOpen={deletingItem != null}
        title="明細の削除"
        message={`${deletingItem?.sequence} 行目「${deletingItem?.item_name}」を削除します。\nこの操作は取り消せません。よろしいですか？`}
        confirmLabel="削除"
        isDanger
        isBusy={removeItem.isPending}
        onClose={() => setDeletingItem(null)}
        onConfirm={() => {
          if (deletingItem) removeItem.mutate(deletingItem);
        }}
      />

      <ConfirmDialog
        isOpen={deletingProject}
        title="案件の削除"
        message={`案件 ${project.project_number} を明細ごと削除します。\nこの操作は取り消せません。よろしいですか？\n\n出荷済みの案件は削除できません。`}
        confirmLabel="削除"
        isDanger
        isBusy={removeProject.isPending}
        onClose={() => setDeletingProject(false)}
        onConfirm={() => removeProject.mutate()}
      />
      <ColumnSettingsDialog
        isOpen={columnSettingsOpen}
        onClose={() => setColumnSettingsOpen(false)}
        table={table}
        onReset={resetColumns}
      />

      <PurchaseOrderUploadDialog
        projectId={id}
        isOpen={uploadingPurchaseOrder}
        onClose={() => setUploadingPurchaseOrder(false)}
        onUploaded={() =>
          void queryClient.invalidateQueries({ queryKey: ["purchase-orders", id] })
        }
      />
    </div>
  );
}
